/* eslint-disable import/no-unresolved */
/* eslint-disable import/extensions */
/**
 * @file This file demostrate the "odata.batch way" to get bundle country and locations.
 *  It is not recommended to use this way only if it's necessary.
 *
 */
import _ from 'underscore';
import * as languageBundle from '@bingads-webui-campaign-legacy/location-targets/util/languageBundle';
import { edmCampaign } from '@bingads-webui/edm-campaign';
import pageContext from '@bingads-webui-static-legacy/page-context';
import odata from '@bingads-webui-component-legacy/http/channel/odata';
import config from '@bingads-webui-component-legacy/config/index';

import { LocationTargetTypes } from './location-target-types';

function getLocalizedTargetType(i18n, { targetType, countryCode }) {
  const isMicrosoftBingDMAEnabled = _.result(pageContext.Permissions.dynamic, 'UpdateNielsenToMicrosoftBingDMA', false);
  const locationDMAString = isMicrosoftBingDMAEnabled ? i18n.getString(_TL_('LocationTargeting_TargetTypeMicrosoftBingDMA')) : i18n.getString(_TL_('LocationTargeting_TargetTypeDMA'));

  switch (targetType) {
    case 'Country':
      return i18n.getString(_TL_('LocationTargeting_TargetTypeCountry'));
    case 'City':
      return i18n.getString(_TL_('LocationTargeting_TargetTypeCity'));
    case 'MetroArea':
      return countryCode === 'US' ? locationDMAString : i18n.getString(_TL_('LocationTargeting_TargetTypeMetroArea'));
    case 'SubGeography':
      return i18n.getString(_TL_('LocationTargeting_TargetTypeSubGeography'));
    case 'PostalCode':
      return i18n.getString(_TL_('LocationTargeting_TargetTypePostalCode'));
    case 'County':
      return countryCode === 'US' ? i18n.getString(_TL_('LocationTargeting_TargetTypeCounty')) : 'N/A';
    case 'Neighborhood':
      return i18n.getString(_TL_('LocationTargeting_TargetTypeNeighborhood'));
    default:
      return 'N/A';
  }
}

export const distanceUnitMap = {
  km: 1,
  kilometers: 'km',
  Kilometers: 'km',
  mi: 0,
  miles: 'mi',
  Miles: 'mi',
  1: 'km',
  0: 'mi',
};

export const getI18nUnitMap = i18n => ({
  km: i18n.getString(_TL_('Location_Target_RadiusUnit_Km')),
  mi: i18n.getString(_TL_('Location_Target_RadiusUnit_Mi')),
});

export function radiusTargetNameTypeComboGenerator(i18n, { distance, unit, name }) {
  const i18nUnitMap = getI18nUnitMap(i18n);

  return i18n.getString(
    _TL_('New_LocationTargeting_RadiusTargetTitleTemplate'),
    { distance, unit: i18nUnitMap[unit] || i18nUnitMap[distanceUnitMap[unit]], name }
  );
}

export function areaTargetNameTypeComboGenerator(i18n, { name, type, countryCode }) {
  return `${name} (${getLocalizedTargetType(i18n, { targetType: type, countryCode })})`;
}

export function buildLocationCriterionFromSearchResult(i18n, { targetItem, targetType }) {
  if (targetType.mode === 'radius') {
    return {
      Status: 'Active',
      SelectionStatus: LocationTargetTypes.New,
      LesLocationType: targetItem.LesLocationType,
      Campaign: null,
      CriterionType: 'Radius',
      Criterion: {
        Type: 'Radius',
        BidAdjustment: 0,
        OriginalBidAdjustment: 0,
        OriginalCashbackPercent: undefined,
        RadiusCriterion: {
          LocationId: targetItem.Id,
          Radius: targetType.radius,
          RadiusUnit: targetType.unit,
          Name: targetItem.ExpandedName,
          NameTypeCombo: radiusTargetNameTypeComboGenerator(i18n, {
            distance: targetType.radius,
            unit: targetType.unit,
            name: targetItem.ExpandedName,
          }),
          Latitude: targetItem.GeoPoint.Latitude,
          Longitude: targetItem.GeoPoint.Longitude,
          CountryCode: targetItem.CountryCode,
          LocationTargetEnclosingLocationIds: _.pluck(targetItem.ParentLocations, 'Id'),
          Type: 'Radius',
          LocationTargetType: targetItem.Type,
        },
      },
    };
  }

  return {
    Status: 'Active',
    SelectionStatus: targetType.status ? targetType.status : LocationTargetTypes.New,
    Campaign: null,
    CriterionType: 'Location',
    Criterion: {
      Type: 'Location',
      BidAdjustment: 0,
      OriginalBidAdjustment: 0,
      OriginalCashbackPercent: undefined,
      LocationCriterion: {
        LocationId: targetItem.Id,
        Name: targetItem.ExpandedName,
        NameTypeCombo: areaTargetNameTypeComboGenerator(i18n, {
          name: targetItem.ExpandedName,
          type: targetItem.Type,
          countryCode: targetItem.CountryCode,
        }),
        Latitude: targetItem.GeoPoint.Latitude,
        Longitude: targetItem.GeoPoint.Longitude,
        CountryCode: targetItem.CountryCode,
        LocationType: targetItem.Type,
        EnclosingLocationIds: _.pluck(targetItem.ParentLocations, 'Id'),
        Type: 'Location',
        LesLocationType: targetItem.LesLocationType,
      },
    },
  };
}

export function getBundleCountries(language, market) {
  let bundleCountries = languageBundle.getBundle(language, market);

  if (!bundleCountries) {
    bundleCountries = [];
    let publisherCountry = languageBundle.getPublisherCountryByCode(market);

    if (_.isUndefined(publisherCountry)) {
      publisherCountry = {
        Country: 'US',
        LocationId: 190,
      };
    }

    bundleCountries.push(publisherCountry);
  }
  return bundleCountries;
}

export function getBundleCountriesLocations(bundleCountries, lcid, cid, aid) {
  const locationIds = _.pluck(bundleCountries, 'LocationId');
  if (_.size(locationIds) === 0) {
    return Promise.resolve([]);
  }

  const reqs = _.map(locationIds, id => ({
    type: 'GET',
    url: `${edmCampaign.Customers.$withKey(cid).Accounts.$withKey(aid).Locations.$withKey(id).path}?%24expand=ParentLocations`,
    headers: lcid ? {
      lcid,
    } : undefined,
  }));

  return odata.batch({ reqs, needContentType: true });
}

export function getBundleCountriesAndBundleLocationCriterions(cid, aid, lcid, language, market, i18n) {
  const bundleCountries = getBundleCountries(language, market);

  return getBundleCountriesLocations(bundleCountries, lcid, cid, aid).then((response) => {
    const bundleLocationCriterions = [];
    if (response.message) {
      throw new Error(response.message);
    }
    _.each(response, (location) => {
      bundleLocationCriterions.push(buildLocationCriterionFromSearchResult(i18n, {
        targetItem: location.data,
        targetType: { mode: 'location', status: 'Included' },
      }));
    });
    return ({ bundleCountries, bundleLocationCriterions });
  }).catch(() => ({ bundleCountries: [], bundleLocationCriterions: [] }));
}

export function loadBundleCountries(serverContext, cid, aid, i18n) {
  const isLocal = () => {
    const hostname = document.location.hostname.toLowerCase();
    return hostname.indexOf('ads.microsoft.com') === -1 &&
        hostname.indexOf('ads-int.microsoft.com') === -1;
  };

  const odataPath = isLocal() ? 'Advertiser/V2' : 'Campaign/V2';

  // multiPart path is inside the request POST body so can't be proxied by UI server like path
  const batchPath = 'Advertiser/V2';

  const {
    ServicesEndpoints: { CampaignAPI, AdInsightAPI },
    CurrentUser = {},
  } = serverContext;
  const lcid = CurrentUser.LocaleID || '1033';
  const market = CurrentUser.Market || 'US';
  const language = 'All';

  config.set({
    'odata.path': odataPath,
    'odata.multipart_path': batchPath,
    'odata.batch_url': `${CampaignAPI}${odataPath}/$batch`,
    'odata.url': CampaignAPI,
    'adinsight.odata.url': AdInsightAPI,
    'adinsight.odata.batch_url': `${AdInsightAPI}AdInsight/V2/$batch`,
    Token: serverContext.Token,
    'odata.token': serverContext.Token,
  });

  return getBundleCountriesAndBundleLocationCriterions(cid, aid, lcid, language, market, i18n);
}
